import { useCreateOrUpdateArtworkInBibliography } from "@/api/related-fields";
import { LinkableType } from "@/components/LinksManagement";
import {
  applyFieldConfigOverrides,
  FormLayoutElement,
} from "@/config/forms/general";
import { ArtworkInBibliography } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";

const getDefaultValues = (data: ArtworkInBibliography) => ({
  bibliography: {
    bibliographieDescriptionNumeroBibliographie:
      data.bibliography?.bibliographieDescriptionNumeroBibliographie ?? "",
    bibliographieDescriptionResumeLong:
      data.bibliography?.bibliographieDescriptionResumeLong ?? "",
  },
  oeuvresDansBibliographieDescriptionReproduitPage:
    data.oeuvresDansBibliographieDescriptionReproduitPage ?? "",
  oeuvresDansBibliographieDescriptionReproduitPlanche:
    (data as any).oeuvresDansBibliographieDescriptionReproduitPlanche ?? "",
  oeuvresDansBibliographieDescriptionPlancheNonNumerotee:
    (data as any).oeuvresDansBibliographieDescriptionPlancheNonNumerotee ??
    false,
  oeuvresDansBibliographieDescriptionCitePage:
    data.oeuvresDansBibliographieDescriptionCitePage ?? "",
  oeuvresDansBibliographieDescriptionListePage:
    (data as any).oeuvresDansBibliographieDescriptionListePage ?? "",
  oeuvresDansBibliographieDescriptionSousNumero:
    (data as any).oeuvresDansBibliographieDescriptionSousNumero ?? "",
  oeuvresDansBibliographieDescriptionPhotographe:
    data.oeuvresDansBibliographieDescriptionPhotographe ?? "",
  oeuvresDansBibliographieDescriptionReproductionCouleur:
    data.oeuvresDansBibliographieDescriptionReproductionCouleur ?? false,
  oeuvresDansBibliographieDescriptionNonPagine:
    data.oeuvresDansBibliographieDescriptionNonPagine ?? false,
  oeuvresDansBibliographieDescriptionReproductionCouverture:
    (data as any).oeuvresDansBibliographieDescriptionReproductionCouverture ??
    false,
  oeuvresDansBibliographieDescriptionTexteLibre:
    (data as any).oeuvresDansBibliographieDescriptionTexteLibre ?? "",
  oeuvresDansBibliographieNotes: data.oeuvresDansBibliographieNotes ?? "",
});

const ArtworkInBibliographyDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
}: {
  fields: FormLayoutElement[] | undefined;
  data: ArtworkInBibliography;
  queryId: string;
  type: LinkableType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const schema = z
    .object({
    oeuvresDansBibliographieDescriptionReproduitPage: z.string().optional(),
    oeuvresDansBibliographieDescriptionReproduitPlanche: z.string().optional(),
    oeuvresDansBibliographieDescriptionPlancheNonNumerotee: z
      .boolean()
      .optional(),
    oeuvresDansBibliographieDescriptionCitePage: z.string().optional(),
    oeuvresDansBibliographieDescriptionListePage: z.string().optional(),
    oeuvresDansBibliographieDescriptionSousNumero: z.string().optional(),
    oeuvresDansBibliographieDescriptionPhotographe: z.string().optional(),
    oeuvresDansBibliographieDescriptionReproductionCouleur: z
      .boolean()
      .optional(),
    oeuvresDansBibliographieDescriptionNonPagine: z.boolean().optional(),
    oeuvresDansBibliographieDescriptionReproductionCouverture: z
      .boolean()
      .optional(),
    oeuvresDansBibliographieDescriptionTexteLibre: z.string().optional(),
    oeuvresDansBibliographieNotes: z.string().optional(),
    bibliography: z.object({
      bibliographieDescriptionNumeroBibliographie: z.string().optional(),
      bibliographieDescriptionResumeLong: z.string().optional(),
    }),
  })
    .passthrough();

  const { mutate: createOrUpdateArtworkInBibliography } =
    useCreateOrUpdateArtworkInBibliography(queryId, type);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(data),
  });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    const payload = {
      id: data.id,
      artworkId: data.artwork.id,
      bibliographyId: data.bibliography.id,
      ...formData,
    };

    createOrUpdateArtworkInBibliography({
      ...(payload as any),
    });
  };
  const isFromBibliography = type === "bibliography";
  const previewConfig = isFromBibliography
    ? {
        attachments: data.artwork?.attachments ?? [],
        type: "artwork" as const,
        alt:
          data.artwork?.oeuvresDescriptionNumeroEnregistrement ??
          data.artwork?.oeuvresDescriptionTitre,
      }
    : {
        attachments: data.bibliography?.attachments ?? [],
        type: "bibliography" as const,
        alt:
          data.bibliography?.bibliographieDescriptionNumeroBibliographie ??
          data.bibliography?.bibliographieDescriptionTitre,
      };

  const enhancedFields = applyFieldConfigOverrides(fields ?? [], {
    oeuvresDansBibliographieDescriptionTexteLibre: {
      expandOnOverflow: true,
    },
  });

  return (
    <LinksDrawerLayout
      form={form}
      fields={enhancedFields}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={previewConfig}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};

export default ArtworkInBibliographyDrawer;
